/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  delay,
  lastValueFrom,
  retry,
  retryWhen,
  timer,
} from 'rxjs';
import { environment } from '../../environments';
import { Events } from './events.service';
import { Request } from './../model/request';
import { Office } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class RestService {
  public usernameSubject = new BehaviorSubject<string>('');
  public emailSubject = new BehaviorSubject<string>('');
  public officeNameSubject = new BehaviorSubject<string>('');
  public officeIdSubject = new BehaviorSubject<string>('');
  public officesSubject = new BehaviorSubject<Office[]>([]);
  public username$ = this.usernameSubject.asObservable();
  public email$ = this.emailSubject.asObservable();
  public languageSubject = new BehaviorSubject<string>('');
  public language$ = this.languageSubject.asObservable();
  public officeName$ = this.officeNameSubject.asObservable();
  public officeId$ = this.officeIdSubject.asObservable();
  public offices$ = this.officesSubject.asObservable();
  public baseUrl = environment.apiHost + '/';

  // globals
  public username: string;
  public umId: string;
  public officeName: string;
  public selectedLanguage = '';
  private dbUsername = '';

  constructor(public http: HttpClient, public events: Events) {}

  updateUsername(username: string) {
    this.usernameSubject.next(username);
  }

  updateEmail(email: string) {
    this.emailSubject.next(email);
  }
  updateOfficeName(officeName: string) {
    this.officeNameSubject.next(officeName);
  }
  updateOfficeId(officeId: string) {
    this.officeIdSubject.next(officeId);
  }
  updateLanguage(language: string) {
    this.languageSubject.next(language);
  }
  updateOffices(offices: Office[]) {
    this.officesSubject.next(offices);
  }
  saveAnswers(answerId: number, json: any): Promise<any> {
    return this.http
      .post(
        this.baseUrl + 'visits/questionaire/answer/' + answerId,
        json,
        this.getHeaders()
      )

      .toPromise();
  }

  saveVisitData(visitId: number, visitData: any): Promise<any> {
    return this.http
      .patch(this.baseUrl + 'visits/' + visitId, visitData, this.getHeaders())
      .toPromise();
  }

  getSaveAnswerOfflineRequest(answerId: number, json: any): Request {
    return new Request(
      'POST',
      this.baseUrl + 'visits/questionaire/answer/' + answerId,
      json
    );
  }
  getSaveVisitDataOfflineRequest(visitId: number, json: any): Request {
    return new Request('PATCH', this.baseUrl + 'visits/' + visitId, json);
  }

  deletePhoto(photoId): Promise<any> {
    return this.http
      .delete(
        this.baseUrl + 'visits/questionaire/answer/photo/' + photoId,
        this.getHeaders()
      )

      .toPromise();
  }

  getDeletePhotoOfflineRequest(photoId): Request {
    return new Request(
      'DELETE',
      this.baseUrl + 'visits/questionaire/answer/photo/' + photoId,
      ''
    );
  }

  savePhoto(answerId: number, json: any): Promise<any> {
    return this.http
      .put(
        this.baseUrl + 'visits/questionaire/answer/' + answerId + '/photo',
        json,
        this.getHeaders()
      )

      .toPromise();
  }

  getSavePhotoOfflineRequest(answerId: number, json: any): Request {
    return new Request(
      'PUT',
      this.baseUrl + 'visits/questionaire/answer/' + answerId + '/photo',
      json
    );
  }

  updatePhoto(photoId: number, json: any): Promise<any> {
    return this.http
      .patch(
        this.baseUrl + 'visits/questionaire/answer/photo/' + photoId,
        json,
        this.getHeaders()
      )

      .toPromise();
  }

  getUpdatePhotoOfflineRequest(photoId: number, json: any): Request {
    return new Request(
      'PATCH',
      this.baseUrl + 'visits/questionaire/answer/photo/' + photoId,
      json
    );
  }

  deleteComment(commentId): Promise<any> {
    return this.http
      .delete(
        this.baseUrl + 'visits/questionaire/answer/comment/' + commentId,
        this.getHeaders()
      )

      .toPromise();
  }

  getDeleteCommentOfflineRequest(commentId): Request {
    return new Request(
      'DELETE',
      this.baseUrl + 'visits/questionaire/answer/comment/' + commentId,
      ''
    );
  }

  saveComment(answerId: number, json: any): Promise<any> {
    return this.http
      .put(
        this.baseUrl + 'visits/questionaire/answer/' + answerId + '/comment',
        json,
        this.getHeaders()
      )

      .toPromise();
  }

  updateComment(commentId: number, json: any): Promise<any> {
    return this.http
      .patch(
        this.baseUrl + 'visits/questionaire/answer/comment/' + commentId,
        json,
        this.getHeaders()
      )

      .toPromise();
  }

  getUpdateCommentOfflineRequest(commentId: number, json: any): Request {
    return new Request(
      'PATCH',
      this.baseUrl + 'visits/questionaire/answer/comment/' + commentId,
      json
    );
  }

  getSaveCommentOfflineRequest(answerId: number, json: any): Request {
    return new Request(
      'PUT',
      this.baseUrl + 'visits/questionaire/answer/' + answerId + '/comment',
      json
    );
  }

  async authUser(key: string) {
    const url = this.baseUrl + 'authentication/mobile/' + key;

    const response = this.http.get(url);
    return await lastValueFrom(response);
  }

  async authUserPlatform(key: string, platform: string) {
    const url = this.baseUrl + 'authentication/mobile/' + key + '/' + platform;

    const response = await this.http.get(url);
    return await lastValueFrom(response);
  }

  getVisitsPlan(): Promise<any> {
    return this.http
      .get<any>(
        this.baseUrl + 'visits/inspector/' + this.umId,
        this.getHeaders()
      )
      .toPromise();
  }

  getEljakimVisitsPlan(email: string, lastWeek: boolean = false): Promise<any> {
    const params = lastWeek ? '?LastWeek=true' : '';
    return this.http
      .get<any>(
        this.baseUrl + 'mobile/technician/list' + params,
        this.getHeaders()
      )
      .toPromise();
  }

  getNonVisitsPlan(email: string, lastWeek: boolean = false): Promise<any> {
    const params = lastWeek ? '?LastWeek=true' : '';
    return this.http
      .get<any>(
        this.baseUrl + 'mobile/nonvisit/list' + params,
        this.getHeaders()
      )
      .toPromise();
  }

  getQuestionnaire(
    id: number,
    parentObject: string,
    customFormId: number
  ): Promise<any> {
    return this.http
      .get<any>(this.baseUrl + 'custom-forms/v1.0/questionnaires', {
        params: { parentType: parentObject, parentId: id, customFormId },
        headers: this.getHeaders().headers,
      })

      .toPromise();
  }

  getPhotos(answerId: number): Promise<any> {
    return this.http
      .get<any>(
        this.baseUrl + 'visits/questionaire/photo/' + answerId,
        this.getHeaders()
      )

      .toPromise();
  }

  getComments(answerId: number): Promise<any> {
    return this.http
      .get<any>(
        this.baseUrl + 'visits/questionaire/comment/' + answerId,
        this.getHeaders()
      )

      .toPromise();
  }

  getVisitResults(): Promise<any> {
    return this.http
      .get<any>(this.baseUrl + 'dictionary/PC.VISIT.RESULT', this.getHeaders())
      .pipe(
        retryWhen((error) => error.pipe(delay(500))),
        retry(3)
      )
      .toPromise();
  }

  getUsername() {
    return this.username;
  }

  getDBUserName() {
    return this.dbUsername;
  }

  putPendingRequest(request: Request): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (request.requestType === 'PUT') {
        this.http
          .put(request.url, request.body, this.getHeaders())
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else if (request.requestType === 'POST') {
        this.http
          .post(request.url, request.body, this.getHeaders())
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      } else if (request.requestType === 'DELETE') {
        this.http
          .delete(request.url, this.getHeaders())
          .pipe(
            retryWhen((error) => error.pipe(delay(500))),
            retry(3)
          )
          .toPromise()
          .then((data) => {
            resolve(true);
          })
          .catch((error) => {
            resolve(false);
          });
      }
    });
  }

  getHeaders() {
    const headers = new HttpHeaders().append(
      'Content-Type',
      'application/json'
    );
    const httpOptions = {
      headers,
    };
    return httpOptions;
  }

  checkMobileVersion(): Observable<CheckVersionModel> {
    return this.http.get<CheckVersionModel>(
      `${this.baseUrl}mobile/check-version`
    );
  }

  changeLanguage(language: string): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}mobile/change-language`,
      { language },
      this.getHeaders()
    );
  }
}

interface CheckVersionModel {
  correctVersion: boolean;
}
